import {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import { ApiDestination } from '../llm-common/llm-types';
import { displayNameToOptionWithDeleted } from 'services/helpers';
import {
  checkDeleted,
  displayNameDescriptor,
  nameDescriptor,
} from 'pages/AutomationEngine/helpers';
import {
  DEFAULT_CONFLICT_RESOLUTION,
  DESTINATION_TYPES,
} from '../llm-common/constants';
import { CallLlmStep } from '../actionSectionTypes';
import type { MCPServer } from '@kizen/api/mcp';

type ApiObject = {
  modelName: string;
  businessPluginApp: any;
  prompt: string;
  htmlPrompt: string;
  mergeFieldValidation: any;
  destinations: ApiDestination[];
  isAdvanced: boolean;
  mcpServer: SnakeToCamelCase<MCPServer> | null;
  runToolsAs: { id: string; name: string; deleted?: boolean } | null;
  maxSequentialTurns: number;
};

const forFlow = (
  apiObject: ApiObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  const {
    modelName,
    businessPluginApp,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    destinations,
    isAdvanced,
    mcpServer,
    runToolsAs,
    maxSequentialTurns,
  } = apiObject;

  if (!modelName || !businessPluginApp) {
    addErrorMessage(messageDictionary.llmPluginAppIsMissing);
  }

  if (mcpServer) {
    checkDeleted(messageDictionary, addErrorMessage, mcpServer, nameDescriptor);
    checkDeleted(
      messageDictionary,
      addErrorMessage,
      runToolsAs,
      nameDescriptor,
      messageDictionary.runToolsAs
    );
  }

  const flow = {
    modelName: modelName || null,
    businessPluginAppId: businessPluginApp?.id || null,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    destinations: destinations.map(
      ({
        relatedObjectField,
        field,
        variable,
        conflictResolution,
        isRequired,
        confidenceThreshold,
        options,
      }) => {
        checkDeleted(
          messageDictionary,
          addErrorMessage,
          relatedObjectField,
          displayNameDescriptor
        );

        checkDeleted(
          messageDictionary,
          addErrorMessage,
          field,
          displayNameDescriptor
        );

        return {
          field: field
            ? displayNameToOptionWithDeleted(field, messageDictionary.deleted)
            : null,
          relatedObjectField:
            !field?.deleted && relatedObjectField // if field is deleted always return null
              ? displayNameToOptionWithDeleted(
                  relatedObjectField,
                  messageDictionary.deleted
                )
              : null,
          variable: variable
            ? displayNameToOptionWithDeleted(
                { ...variable, displayName: undefined },
                messageDictionary.deleted
              )
            : null,
          conflictResolution: conflictResolution || DEFAULT_CONFLICT_RESOLUTION,
          isRequired: isRequired || false,
          options: (options || []).map((option) =>
            displayNameToOptionWithDeleted(option, messageDictionary.deleted)
          ),
          confidenceThreshold,
          destinationType: relatedObjectField
            ? DESTINATION_TYPES.related_object_field
            : variable
              ? DESTINATION_TYPES.variable
              : DESTINATION_TYPES.this_object_field,
        };
      }
    ),
    isAdvanced,
    mcpServer,
    runToolsAs: mcpServer
      ? runToolsAs ?? { id: '', name: '', deleted: true }
      : null,
    maxSequentialTurns,
  };

  return flow;
};

const forApi = (
  step: CallLlmStep,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  const {
    modelName,
    businessPluginAppId,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    destinations,
    isAdvanced,
    mcpServer,
    runToolsAs,
    maxSequentialTurns,
  } = step.details;

  const api = {
    ...step,
    details: {
      modelName,
      businessPluginAppId,
      prompt,
      htmlPrompt,
      mergeFieldValidation,
      destinations: destinations.map((destination) => {
        const {
          relatedObjectField,
          field,
          variable,
          conflictResolution,
          options,
          isRequired,
          confidenceThreshold,
        } = destination;
        return {
          relatedObjectField: relatedObjectField?.value,
          field: field?.value,
          variable: variable?.label,
          conflictResolution: conflictResolution || DEFAULT_CONFLICT_RESOLUTION,
          options: options.map((option) => option.value),
          isRequired,
          confidenceThreshold,
        };
      }),
      isAdvanced,
      mcpServerId: mcpServer?.id || null,
      runToolsAsId: runToolsAs?.id || null,
      maxSequentialTurns,
    },
  };

  return api;
};

export const callLlmDTO = { forApi, forFlow };
